import { useEffect, useRef, useState } from 'react'

/**
 * Dropping files anywhere on the page while the agent is open.
 *
 * THE LISTENER IS ON THE WINDOW, NOT THE LAYER, and that is not a
 * preference. The agent surface is `pointer-events: none` so the page
 * underneath stays clickable, and `pointer-events: none` drops DRAG events
 * as well as clicks. A file dropped on the layer therefore lands on the
 * page behind it, and the browser's default for that is to navigate away
 * and render the image. Nothing warns you; the conversation is simply
 * gone. So the window hears the drag, and the layer only paints a target.
 *
 * THE COUNTER IS THE OTHER TRAP. `dragleave` fires every time the pointer
 * crosses into a child element, so a naive "leave means gone" flickers the
 * target off and on all the way across the screen. Counting enters against
 * leaves is the standard fix and the only one that survives a page full of
 * nested nodes.
 *
 * It only arms while the surface is open: a page-wide drop target on every
 * Connect screen would be a page-wide drop target nobody asked for.
 */
export function useFileDrop(
  active: boolean,
  onFiles: (files: File[]) => void,
): boolean {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  // Through a ref so a changing callback does not rebind the listeners
  // mid-drag, which would lose the counter and strand the target.
  const sink = useRef(onFiles)
  sink.current = onFiles

  useEffect(() => {
    if (!active) {
      setDragging(false)
      depth.current = 0
      return
    }

    /**
     * Files, not a text selection.
     *
     * Dragging a highlighted booking reference across the screen also
     * fires these events, and painting a file target for it would be
     * telling somebody they can do something they cannot.
     */
    const isFileDrag = (event: DragEvent) =>
      [...(event.dataTransfer?.types ?? [])].includes('Files')

    const onEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return
      depth.current += 1
      setDragging(true)
    }

    const onOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return
      // Required: without preventDefault on dragover the drop never
      // happens at all and the browser navigates instead.
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    }

    const onLeave = (event: DragEvent) => {
      if (!isFileDrag(event)) return
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setDragging(false)
    }

    const onDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return
      event.preventDefault()
      depth.current = 0
      setDragging(false)
      const files = [...(event.dataTransfer?.files ?? [])]
      if (files.length > 0) sink.current(files)
    }

    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragover', onOver)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [active])

  return dragging
}
